import React, { useState, useRef, useEffect } from 'react'
import { isDropdownAnswer, Image, useImageDimensions } from '../../utils'
import { EMPTY_INITIAL_ANSWER } from '../../utils/constants'
import {
	getImageDropdownKey,
	ImageLabeling as ImageLabelingType,
	ClientFields,
	StudentAnswer,
	QuestionChoice,
	variantTypes,
} from '@mission.io/question-toolkit'
import { DropdownImageLabeling, ClassResponse } from '../../model/general'
import { Position } from '../../assets/componentImages'
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai'
import PopoverWrapper from '../popover'
import 'styled-components/macro'
import { styled, useTheme } from 'styled-components'
import OptionalMarkdown from '../Markdown/OptionalMarkdown'
import { _tempTheme } from '../../utils/tempTheme'

const initialValue = EMPTY_INITIAL_ANSWER
interface ImageWrapperProps {
	question: ImageLabelingType<ClientFields>
	handleSelectOption?: (index: number) => void
	studentAnswer?: StudentAnswer
	isReview?: boolean
	isQuestionSlot?: boolean
	isReadonly?: boolean
	optionsWrapper?: React.ReactNode
	optionsStats?: React.ReactNode
	className?: string
	parentRef?: React.MutableRefObject<HTMLElement | undefined>
	classResponse?: ClassResponse | null
	dropdownIndexSelected?: number
}

//Component that display a resized image with dropdown icons to fit the parent component for Student views
export default function ImageWrapper(props: ImageWrapperProps): JSX.Element {
	const { question, className, ...dropdownProps } = props

	const imgWrapper = useRef<HTMLImageElement | null>(null)

	const dimensions = useImageDimensions(imgWrapper, question.media[0]?.url)

	const setStyle =
		question.imageProperties.width > question.imageProperties.height
			? `width: ${question.imageProperties.width}px; max-width: min(60vw, 100%);`
			: `height: ${question.imageProperties.height}px; max-height: 60vh;`

	return (
		<ImageWrapperStyled className={className} aria-label="Image wrapper">
			<div className="content-wrapper">
				{
					<>
						<Image ref={imgWrapper} media={question.media} css={setStyle} />
						<Dropdowns
							question={question}
							dimensions={dimensions}
							{...dropdownProps}
						/>
					</>
				}
			</div>
		</ImageWrapperStyled>
	)
}

export function Dropdowns({
	question,
	dimensions,
	handleSelectOption,
	studentAnswer,
	isReview = false,
	optionsWrapper,
	isQuestionSlot = false,
	isReadonly = false,
	parentRef,
	classResponse,
	optionsStats,
	dropdownIndexSelected,
}: {
	question: ImageLabelingType<ClientFields>
	dimensions: null | { width: number; height: number; src: string }
	handleSelectOption?: ((index: number) => void) | undefined
	studentAnswer?: StudentAnswer | undefined
	isReview?: boolean
	isQuestionSlot?: boolean | undefined
	isReadonly?: boolean
	optionsWrapper?: React.ReactNode
	optionsStats?: React.ReactNode | undefined
	parentRef?: React.MutableRefObject<HTMLElement | undefined>
	classResponse?: ClassResponse | null
	dropdownIndexSelected?: number
}) {
	const [ratios, setRatios] = useState<null | { x: number; y: number }>(null)

	useEffect(() => {
		if (question && dimensions && question.media[0]?.url === dimensions.src) {
			setRatios({
				x: question.imageProperties.width / dimensions.width,
				y: question.imageProperties.height / dimensions.height,
			})
		} else {
			setRatios(null)
		}
	}, [dimensions, question])

	if (!ratios) {
		return null
	}

	return (
		<>
			<UlNoStyle>
				{question.dropdowns.map(
					(item: DropdownImageLabeling, index: number) => {
						const choiceCorrect = item.choices.find(choice => choice.answer)
						const classResponseOptions =
							classResponse &&
							classResponse.scores.type === variantTypes.IMAGE_LABELING &&
							classResponse.scores.optionScores.find(
								i => i.templateResponsekey === getImageDropdownKey(item)
							)

						return (
							<DropdownOption
								key={`dropdown-option-${index}`}
								handleClick={() =>
									handleSelectOption && handleSelectOption(index)
								}
								value={
									studentAnswer && isDropdownAnswer(studentAnswer)
										? item.choices.find(
												choice =>
													String(choice._id) ===
													(isReview
														? studentAnswer.value[getImageDropdownKey(item)]
														: studentAnswer.value[getImageDropdownKey(item)])
										  )?.text
										: null
								}
								x={item.x}
								y={item.y}
								ratios={ratios}
								choiceCorrect={choiceCorrect}
								isReview={isReview}
								optionsWrapper={optionsWrapper}
								isCorrect={
									studentAnswer &&
									isDropdownAnswer(studentAnswer) &&
									String(choiceCorrect?._id) ===
										studentAnswer.value[getImageDropdownKey(item)]
								}
								index={index}
								isReadonly={isReadonly}
								isQuestionSlot={isQuestionSlot}
								parentRef={parentRef}
								classResponseOptions={
									classResponseOptions ? classResponseOptions.choices : []
								}
								dropdownIndexSelected={dropdownIndexSelected}
							/>
						)
					}
				)}
			</UlNoStyle>
			{optionsStats}
		</>
	)
}

const UlNoStyle = styled.ul`
	padding-inline-start: 0;
	margin-block-end: 0;
	margin-block-start: 0;
`

//This function draws the dropdown icon on the image labeling
function DropdownOption(props: {
	x: number
	y: number
	value?: string | null
	isCorrect?: boolean
	isReview?: boolean
	handleClick?: () => void
	ratios: { x: number; y: number }
	optionsWrapper: React.ReactNode
	isQuestionSlot?: boolean
	choiceCorrect?: QuestionChoice
	parentRef?: React.MutableRefObject<HTMLElement | undefined>
	isReadonly?: boolean
	index: number
	classResponseOptions: Array<{
		_id: string
		score: number
	}>
	dropdownIndexSelected?: number
}): JSX.Element {
	const {
		x,
		y,
		value: _value,
		handleClick,
		isReview = false,
		isCorrect = false,
		ratios,
		optionsWrapper,
		isQuestionSlot = false,
		choiceCorrect,
		parentRef,
		isReadonly = false,
		index,
		classResponseOptions,
		dropdownIndexSelected,
	} = props
	let value = _value
	if (!value) {
		if (isReview) value = choiceCorrect?.text ?? ''
		else value = initialValue
	}

	// variable to center the text

	const offsetLeft = (value.length < 7 ? 15 : -15) / 16 //This equation was obtained by tabulating the length of the responses with their position

	const theme = useTheme()
	if (isReadonly)
		return (
			<Option //These offsets are gotten from the dimensions of the image, reducing by the degree of inclination.
				style={{
					left: `${x / ratios.x}px`,
					top: `${y / ratios.y}px`,
				}}>
				<div>
					<CircleIcon>{index + 1}</CircleIcon>
				</div>
			</Option>
		)

	const stat = classResponseOptions.find(
		i => i._id === String(choiceCorrect?._id)
	)
	const iconRef = useRef<HTMLDivElement | null>(null)
	const [positionIconSize, setPositionIconSize] = useState({
		height: 0,
		width: 0,
	})
	useEffect(() => {
		if (iconRef.current) {
			setPositionIconSize({
				width: iconRef.current.clientWidth + 10,
				height: iconRef.current.clientHeight + 16,
			})
		}
	}, [iconRef])

	return (
		<Option
			style={{
				left: `calc(${x / ratios.x}px - 42px)`,
				top: `calc(${y / ratios.y}px - 45px)`,
			}}>
			<div>
				{classResponseOptions.length ? (
					<div onClick={handleClick}>
						<div>
							<Answer
								$offsetLeft={offsetLeft}
								$status="REVIEWED"
								$isReview
								$isSelected={dropdownIndexSelected === index}>
								<OptionalMarkdownWrapper className="text-value">
									<OptionalMarkdown>
										{choiceCorrect?.text ?? ''}
									</OptionalMarkdown>
								</OptionalMarkdownWrapper>
							</Answer>
						</div>
						<IconWrapper ref={iconRef}>
							<span className="icon text-value">{`${
								stat ? (stat.score * 100).toFixed() : 0
							}%`}</span>
							<Position
								color="success"
								opacity="1"
								width={positionIconSize.width}
								height={positionIconSize.height}
							/>
						</IconWrapper>
					</div>
				) : isQuestionSlot ? (
					<>
						<Input
							className="value"
							style={{ cursor: 'default' }}
							$offsetLeft={offsetLeft}
							$isQuestionSlot={isQuestionSlot}
							$isReview={false}>
							<OptionalMarkdownWrapper className="text-value">
								<OptionalMarkdown>{value}</OptionalMarkdown>
							</OptionalMarkdownWrapper>
						</Input>
						<IconWrapper
							ref={iconRef}
							style={{ cursor: 'default' }}
							$isReview={false}>
							{isReview ? (
								isCorrect ? (
									<AiOutlineCheck className="icon" />
								) : (
									<AiOutlineClose className="icon" />
								)
							) : null}
							<Position
								opacity="0.5"
								css="cursor: default;"
								color="default"
								width={positionIconSize.width}
								height={positionIconSize.height}
							/>
						</IconWrapper>
					</>
				) : isReview ? (
					<PopoverWrapper
						padding={theme.spacing2xValue}
						handleClickTrigger={handleClick}
						parentElement={parentRef?.current}
						trigger={
							<div
								css={
									isCorrect ? '' : `padding-bottom: ${_tempTheme.spacing6};`
								}>
								<div>
									<Answer
										$offsetLeft={offsetLeft}
										$status={
											_value
												? isCorrect
													? 'CORRECT'
													: 'INCORRECT'
												: 'NO_ANSWER'
										}
										$isReview>
										<OptionalMarkdownWrapper className="text-value">
											<OptionalMarkdown>{value}</OptionalMarkdown>
										</OptionalMarkdownWrapper>
									</Answer>
									{!isCorrect && (
										<Answer
											$status={'CORRECT'}
											$offsetLeft={offsetLeft}
											$isReview>
											<OptionalMarkdownWrapper className="text-value">
												<OptionalMarkdown>
													{choiceCorrect?.text ?? ''}
												</OptionalMarkdown>
											</OptionalMarkdownWrapper>
										</Answer>
									)}
								</div>
								<IconWrapper ref={iconRef}>
									{isCorrect ? (
										<AiOutlineCheck className="icon" />
									) : (
										<AiOutlineClose className="icon" />
									)}
									<Position
										color={isCorrect ? 'success' : 'error'}
										width={positionIconSize.width}
										height={positionIconSize.height}
										opacity="1"
									/>
								</IconWrapper>
							</div>
						}>
						{optionsWrapper}
					</PopoverWrapper>
				) : (
					<PopoverWrapper
						padding={theme.spacing2xValue}
						handleClickTrigger={handleClick}
						parentElement={parentRef?.current}
						trigger={
							<>
								<Input $offsetLeft={offsetLeft} $isReview={false}>
									<OptionalMarkdownWrapper className="text-value">
										<OptionalMarkdown>{value}</OptionalMarkdown>
									</OptionalMarkdownWrapper>
								</Input>
								<IconWrapper ref={iconRef} $isReview={false}>
									<Position
										color="default"
										opacity="0.5"
										width={positionIconSize.width}
										height={positionIconSize.height}
									/>
								</IconWrapper>
							</>
						}>
						{optionsWrapper}
					</PopoverWrapper>
				)}
			</div>
		</Option>
	)
}

const OptionalMarkdownWrapper = styled.div`
	display: inline;
`

const IconWrapper = styled.div<IReview>`
	width: 2.5em;
	height: 3em;
	cursor: pointer;
	display: flex;
	justify-content: center;
	background-color: transparent;
	margin: 0;
	position: relative;
	border-radius: ${_tempTheme.spacing6};
	.text-value {
		margin-left: -6px;
    font-size: ${_tempTheme.text_xs};
	}
}
`
interface IReview {
	$isReview?: boolean
	$offsetLeft?: number
	$isQuestionSlot?: boolean
}

const Input = styled.div<IReview>`
	position: absolute;
	cursor: pointer;
	transform: rotate(355deg)
		translate(${({ $offsetLeft }) => $offsetLeft}em, 3.2em);
	top: 5%;
	width: max-content;
	background: rgb(
		5,
		171,
		167,
		${({ $isReview }) => (!$isReview ? '0.5' : '1')}
	);
	border-radius: ${_tempTheme.spacing1};
	font-style: normal;
	font-weight: ${_tempTheme.font_bold};
	font-size: ${_tempTheme.text_sm};
	font-family: ${({ theme }) => theme.fontFamily};
	color: ${({ theme }) => theme.white};
	line-height: ${_tempTheme.text_lg};
	text-align: center;
	padding: 2px 6px;
	max-width: 100px;
	display: inline-block;

	.text-value {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	display: ${({ $isQuestionSlot }) => ($isQuestionSlot ? 'none' : 'flex')};
	&:hover {
		${({ $isQuestionSlot }) =>
			!$isQuestionSlot &&
			`
		background:   rgb(5, 171, 167, 1);
		`}
	}
`

interface IImageWrapperStyled {
	$isQuestionSlot?: boolean
}
const ImageWrapperStyled = styled.div<IImageWrapperStyled>`
	.content-wrapper {
		max-width: min(100%, 500px);
		height: auto;
		max-height: 100%;
		margin: auto;
		position: relative;
	}
	max-height: 100%;
	background: ${({ theme }) => theme.base};
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`
const Option = styled.li`
	position: absolute;
	display: block;
	margin: auto;

	.icon {
		left: ${_tempTheme.spacing4};
		position: absolute;
		top: ${_tempTheme.spacing4};
		color: ${({ theme }) => theme.white};
		stroke-width: ${_tempTheme.spacing16};
	}
	.value {
		background-color: ${({ theme }) => theme.success};
	}
`

const Answer = styled(Input)<{
	$status: 'CORRECT' | 'INCORRECT' | 'NO_ANSWER' | 'REVIEWED'
	$isSelected?: boolean
}>`
	&:last-of-type:not(:first-of-type) {
		left: -45%;
		top: 40%;
	}
	&:first-of-type:not(:last-of-type) {
		left: -65%;
		top: 2%;
	}
	${({ $status: status, $isSelected: isSelected, theme }) => {
		const color =
			status === 'CORRECT'
				? theme.success
				: status === 'REVIEWED'
				? '#05ABA7'
				: theme.error
		return `
		background-color: ${color};
		text-decoration: ${status === 'INCORRECT' ? 'line-through' : 'inherit'};
		&:hover {
			filter: brightness(75%);
			background-color: ${color};
		}
		border: ${`${_tempTheme.spacing1} solid ${
			isSelected ? theme.lightSuccess : color
		}`};
		`
	}}
`
const CircleIcon = styled.div`
	background: ${({ theme }) => theme.primary};
	color: ${({ theme }) => theme.neutralLightest};
	cursor: default;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	width: ${_tempTheme.spacing6};
	height: ${_tempTheme.spacing6};
`
